import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";
import { toast } from "react-toastify";
import api from "../../config/URL";
import GlobalDelete from "../../components/common/GlobalDelete";

/* ================= CSV EXPORT HELPER ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "Package_Balance_Report.csv"
) => {
  // columns NOT allowed in CSV
  const EXCLUDED_KEYS = ["id"];

  const exportableColumns = table
    .getAllLeafColumns()
    .filter(
      (col) =>
        col.getIsVisible() &&
        col.columnDef.accessorKey &&
        !EXCLUDED_KEYS.includes(col.columnDef.accessorKey)
    );

  const headers = exportableColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    exportableColumns.map((col) => {
      let value = row[col.columnDef.accessorKey];

      // format date columns for Excel
      if (
        col.columnDef.accessorKey === "startDate" ||
        col.columnDef.accessorKey === "endDate" ||
        col.columnDef.accessorKey === "createdAt" ||
        col.columnDef.accessorKey === "updatedAt"
      ) {
        value = value ? new Date(value).toLocaleDateString("en-GB") : "";
      }

      return `"${value ?? ""}"`;
    })
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ==================================================== */

const PackageBalanceReport = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    courseId: "",
    studentId: "",
  });

  const [courseData, setCourseData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "",
        enableHiding: true,
        enableSorting: false,
        size: 20,
        Cell: ({ cell, row }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
              setSelectedStudentId(row.original.studentId);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "centerName",
        header: "Centre",
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: "studentName",
        header: "Student",
        enableHiding: true,
        size: 120,
      },
      {
        accessorKey: "courseName",
        header: "Course",
        enableHiding: true,
        size: 120,
      },
      {
        accessorKey: "packageName",
        header: "Package",
        enableHiding: true,
        size: 120,
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => {
          const startDate = cell.getValue();
          if (!startDate) return "--";
          return new Date(startDate).toLocaleDateString("en-GB");
        },
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => {
          const endDate = cell.getValue();
          if (!endDate) return "--";
          return new Date(endDate).toLocaleDateString("en-GB");
        },
      },
      {
        accessorKey: "remainingLessons",
        header: "Remaining Lessons",
        enableHiding: true,
        size: 100,
        Cell: ({ cell }) => cell.getValue() || "0",
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      { accessorKey: "updatedBy", header: "Updated By" },
    ],
    []
  );

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#e6edf7 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const getPackageBalanceData = async () => {
    try {
      setLoading(true);

      // Build query params
      const queryParams = new URLSearchParams();

      // centerId ALWAYS from selectedCenter
      if (selectedCenter) {
        queryParams.append("centerId", selectedCenter);
      }

      // courseId & studentId only if selected
      if (filters.courseId) {
        queryParams.append("courseId", filters.courseId);
      }

      if (filters.studentId) {
        queryParams.append("studentId", filters.studentId);
      }

      const response = await api.get(
        `/packageBalanceReportOld?${queryParams.toString()}`
      );

      // attach stable row id
      const formattedData = (response.data || []).map((item, index) => ({
        ...item,
        id: `${item.centerId}-${item.studentId}-${item.courseId}-${index}`,
      }));

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch package balance report");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      centerId: selectedCenter,
      courseId: "",
      studentId: "",
    });
  };

  const fetchListWithCenterIdData = async (centerId) => {
    try {
      const [courseDatas, student] = await Promise.all([
        fetchAllCoursesWithIdsC(centerId),
        fetchAllStudentListByCenter(centerId),
      ]);
      setCourseData(courseDatas || []);
      setStudentData(student || []);
    } catch (error) {
      toast.error("Failed to fetch center-specific data");
      console.error(error);
    }
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);

  useEffect(() => {
    if (filters.centerId) {
      fetchListWithCenterIdData(filters.centerId);
    }
  }, [filters.centerId]);

  useEffect(() => {
    getPackageBalanceData();
  }, [filters, selectedCenter]);
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedId(null);
  };

  return (
    <div className="container-fluid center my-4 px-2">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Report Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Package Balance Report
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Package Balance Report</strong>
          </span>
        </div>
        <div className="d-flex justify-content-between mb-3">
          <div className="d-lg-flex individual_fliters">
            <input type="hidden" name="centerId" value={filters.centerId} />
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="courseId"
                onChange={handleFilterChange}
                value={filters.courseId}
              >
                <option value="">Select Courses</option>
                {courseData?.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.courseNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="studentId"
                onChange={handleFilterChange}
                value={filters.studentId}
              >
                <option value="">Select Students</option>
                {studentData?.map((std) => (
                  <option key={std.id} value={std.id}>
                    {std.studentNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-2 ms-2">
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable
                columns={columns}
                data={data}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                initialState={{
                  pagination: { pageSize: 50, pageIndex: 0 },
                  columnVisibility: {
                    createdBy: false,
                    createdAt: false,
                    updatedBy: false,
                    updatedAt: false,
                  },
                }}
                renderTopToolbarCustomActions={({ table }) => (
                  <button
                    className="btn btn-sm btn-border"
                    onClick={() =>
                      exportVisibleColumnsToCSV(
                        table,
                        data,
                        "Package_Balance_Report.csv"
                      )
                    }
                  >
                    Export CSV
                  </button>
                )}
              />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  navigate(`/invoice/add?studentID=${selectedStudentId}`);
                  handleMenuClose();
                }}
                className="p-2"
              >
                Generate Invoice
              </MenuItem>
              {/* <MenuItem>
                <GlobalDelete
                  path={`/deleteGenerateInvoice/${selectedId}`}
                  onDeleteSuccess={getPackageBalanceData}
                  onOpen={handleMenuClose}
                />
              </MenuItem> */}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default PackageBalanceReport;
